import { MapPinOff } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { destinoDoUsuario } from '@/nucleo/auth/destino';
import { useAuth } from '@/nucleo/auth/useAuth';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/ui/base/card';

/**
 * `*` — endereço que não existe.
 *
 * O caminho de volta depende de quem está olhando: quem tem sessão volta para a
 * própria área (operação ou portal, conforme o `interno` do GET /eu); quem não
 * tem, volta para o portal público. Oferecer "voltar para a operação" a quem não
 * entrou seria mandar a pessoa para uma tela que vai recusá-la.
 *
 * Não usa `ErrorState`: aquele componente traz o próprio `h1`, e aqui a tela é a
 * página inteira — além de precisar de dois caminhos de volta, que dependem da
 * sessão.
 *
 * Separado da moldura (`<main>`/centralização) porque este cartão também
 * aparece DENTRO do shell do backoffice (H-102.A2): um endereço que não bate
 * com nenhum item de menu dentro de `/operacao` ou `/admin` deve renderizar
 * cercado pela barra lateral e pelo cabeçalho, não como página solta — e nesse
 * caso o `<main id="conteudo">` já vem da `CascaDeArea`, um segundo aqui seria
 * `id` duplicado e quebraria o alvo do "pular para o conteúdo".
 */
export function CartaoNaoEncontrado() {
  const { estado, usuario } = useAuth();
  const { pathname } = useLocation();

  const autenticado = estado === 'autenticado' && usuario !== null;
  const minhaArea = autenticado ? destinoDoUsuario(usuario) : null;

  return (
    <Card className="w-full max-w-lg">
      <CardHeader className="space-y-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <MapPinOff className="h-6 w-6" aria-hidden="true" />
        </span>
        {/* `h1` direto: `CardTitle` emite `h3`, e cada tela traz o próprio `h1`. */}
        <h1 className="text-2xl font-bold leading-none tracking-tight">Página não encontrada</h1>
        <CardDescription className="leading-relaxed">
          O endereço <code className="font-mono text-foreground">{pathname}</code> não existe no
          Sistema TEG. Ele pode ter mudado, ou o link que trouxe você até aqui pode estar
          incompleto.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-wrap gap-2">
        {minhaArea && (
          <Button asChild>
            <Link to={minhaArea}>Ir para a minha área</Link>
          </Button>
        )}

        <Button asChild variant={minhaArea ? 'outline' : 'default'}>
          <Link to="/">Ir para o início</Link>
        </Button>

        {!autenticado && (
          <Button asChild variant="outline">
            <Link to="/login">Entrar no sistema</Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/** Endereço fora de qualquer área: sem shell nenhum para herdar, página inteira. */
export function PaginaNaoEncontrada() {
  return (
    <main id="conteudo" className="flex min-h-dvh items-center justify-center px-4 py-10">
      <CartaoNaoEncontrado />
    </main>
  );
}

/**
 * Mesmo cartão, dentro do shell de uma área interna: o `<main id="conteudo">`
 * já é o da `CascaDeArea`, então aqui só a moldura de conteúdo (mesmo padrão de
 * `ModuloEmConstrucao`: `mx-auto max-w-2xl py-8`).
 */
export function PaginaNaoEncontradaNaArea() {
  return (
    <div className="flex flex-1 items-center justify-center py-8">
      <CartaoNaoEncontrado />
    </div>
  );
}

export default PaginaNaoEncontrada;
